/* eslint-disable */
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetOpenEmsLandingTotalsQuery } from '../../redux/api';
import styles from './OpenEmsMonthlyRatesWidget.module.css';
import { OPEN_EMS_UI_URL } from '../../utils/openEmsUrls';
import {
  getCurrentDamRetailUahPerKwhFromLandingTotals,
  toFiniteNumber,
  transformDamToRetailUahPerKwh,
} from './openEmsDamPricing';

function formatLocale(language) {
  const base = typeof language === 'string' ? language.split('-')[0] : '';
  if (base === 'en') return 'en-GB';
  if (base === 'ru') return 'ru-UA';
  return 'uk-UA';
}

function formatMonthLabel(isoDate) {
  if (typeof isoDate !== 'string') return '';
  const [year, month] = isoDate.split('-').map(Number);
  if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) return '';
  return `${String(month).padStart(2, '0')}.${String(year).padStart(4, '0')}`;
}

export default function OpenEmsMonthlyRatesWidget() {
  const { t, i18n } = useTranslation();
  const { data, isFetching } = useGetOpenEmsLandingTotalsQuery();
  const locale = useMemo(
    () => formatLocale(i18n.resolvedLanguage || i18n.language),
    [i18n.language, i18n.resolvedLanguage]
  );

  const display = useMemo(() => {
    const fallback = {
      monthLabel: '',
      priceLabel: isFetching ? '...' : '-',
      prevMonthLabel: '',
      deltaPct: null,
      deltaLabel: null,
    };

    if (!data?.ok || !data?.dam?.configured || !data?.dam?.currentMonthStart) return fallback;

    const useImportWeightedDam = data.exportScope === 'device' || data.exportScope === 'huawei';
    const currentRetail = getCurrentDamRetailUahPerKwhFromLandingTotals(data);
    const prevDam =
      useImportWeightedDam && toFiniteNumber(data.dam.prevMonthDeviceImportWeightedAvgDamUahPerKwh) != null
        ? toFiniteNumber(data.dam.prevMonthDeviceImportWeightedAvgDamUahPerKwh)
        : toFiniteNumber(data.dam.prevAvgUahPerKwh);
    const prevRetail = transformDamToRetailUahPerKwh(prevDam);
    const priceFormatter = new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    const percentFormatter = new Intl.NumberFormat(locale, {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
      signDisplay: 'always',
    });

    const deltaPct =
      currentRetail != null && prevRetail != null && prevRetail > 0
        ? ((currentRetail - prevRetail) / prevRetail) * 100
        : null;

    return {
      monthLabel: formatMonthLabel(data.dam.currentMonthStart),
      priceLabel: currentRetail != null ? priceFormatter.format(currentRetail) : '-',
      prevMonthLabel: formatMonthLabel(data.dam.prevMonthStart),
      deltaPct,
      deltaLabel: deltaPct != null ? `${percentFormatter.format(deltaPct)}%` : null,
    };
  }, [data, isFetching, locale]);

  const deltaClassName =
    display.deltaPct == null
      ? styles.deltaMuted
      : display.deltaPct < 0
        ? styles.deltaDown
        : display.deltaPct > 0
          ? styles.deltaUp
          : styles.deltaMuted;

  const openEmsHref = `${OPEN_EMS_UI_URL.replace(/\/+$/, '')}/`;
  const openEmsLabel = t('b2bOpenEmsMonthlyRatesOpen');

  return (
    <section className={styles.widgetSection} aria-labelledby="open-ems-monthly-rates-title">
      <a
        href={openEmsHref}
        target="_blank"
        rel="noopener noreferrer"
        className={styles.widgetCard}
        aria-label={openEmsLabel}
        title={openEmsLabel}
      >
        <h2 id="open-ems-monthly-rates-title" className={styles.widgetTitle}>
          {t('b2bOpenEmsMonthlyRatesTitle')}
        </h2>
        <p className={styles.widgetSubtitle}>{t('b2bOpenEmsMonthlyRatesSubtitle')}</p>

        <div className={styles.summaryRow}>
          <div className={styles.monthLabel}>{display.monthLabel || t('b2bOpenEmsMonthlyRatesPending')}</div>

          <div className={styles.priceCard}>
            <span className={styles.priceValue}>{display.priceLabel}</span>
            <span className={styles.priceCurrency}>₴</span>
            <span className={styles.priceUnit}>{t('b2bOpenEmsMonthlyRatesUnit')}</span>
          </div>

          <div className={`${styles.deltaWrap} ${deltaClassName}`}>
            {display.deltaLabel != null ? (
              <>
                <span className={styles.deltaValue}>{display.deltaLabel}</span>
                <span className={styles.deltaMeta}>
                  {t('b2bOpenEmsMonthlyRatesVsPrev', { month: display.prevMonthLabel })}
                </span>
              </>
            ) : (
              <span className={styles.deltaMeta}>{t('b2bOpenEmsMonthlyRatesNoComparison')}</span>
            )}
          </div>
        </div>

        <span className={styles.widgetLink}>{openEmsLabel}</span>
      </a>
    </section>
  );
}
